import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

interface ChatAutoScrollOptions {
  containerRef: RefObject<HTMLElement>;
  contentRef: RefObject<HTMLElement>;
  /** A conversation or view change always starts at its newest message. */
  identity?: string;
  itemCount: number;
  enabled?: boolean;
  /**
   * Holds the view still.
   *
   * Set while the transcript is showing something other than its live end — a
   * page loaded around a search hit, a notification's message, older history
   * pulled in above. Following the newest message there would scroll the
   * reader away from the thing they asked to see. Unlike `enabled: false` this
   * does not re-anchor when it clears.
   */
  suspended?: boolean;
}

export interface ChatAutoScrollControls {
  /** Stop following the newest message for `ms`, cancelling any queued scroll. */
  hold: (ms: number) => void;
}

/**
 * Keeps a transcript anchored to its newest message without fighting someone
 * who deliberately scrolled up. ResizeObserver covers late image decoding,
 * voice-player metadata and responsive composer/layout changes—the cases a
 * one-shot `scrollTop = scrollHeight` misses.
 */
export function useChatAutoScroll({
  containerRef,
  contentRef,
  identity,
  itemCount,
  enabled = true,
  suspended = false,
}: ChatAutoScrollOptions): ChatAutoScrollControls {
  const shouldStickRef = useRef(true);
  const frameRef = useRef<number | null>(null);
  /* Read rather than depended on, so clearing the flag does not itself count
     as a reason to scroll. */
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  /** Set by `hold`; auto-scrolling is off until this moment passes. */
  const heldUntilRef = useRef(0);

  const held = () => suspendedRef.current || Date.now() < heldUntilRef.current;

  const scrollToBottom = useCallback(() => {
    if (held()) return;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      // Checked again on the frame: a hold can begin between the two.
      if (held()) return;
      const container = containerRef.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
    });
  }, [containerRef]);

  /**
   * Stops following the newest message for `ms`, and cancels any scroll
   * already queued.
   *
   * Imperative rather than a prop because the caller needs the hold to take
   * effect *before* it starts its own scroll, in the same tick. Going through
   * React state loses the race: a jump is requested from an effect that runs
   * after the layout effect below has already asked for a frame, and that
   * frame would land on the newest message a moment after the jump started
   * moving somewhere else.
   */
  const hold = useCallback((ms: number) => {
    heldUntilRef.current = Math.max(heldUntilRef.current, Date.now() + ms);
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    if (!enabled) return;
    shouldStickRef.current = true;
    scrollToBottom();
  }, [enabled, identity, scrollToBottom]);

  useLayoutEffect(() => {
    if (enabled && shouldStickRef.current) scrollToBottom();
  }, [enabled, itemCount, scrollToBottom]);

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const updateStickiness = () => {
      const distance = container.scrollHeight - container.clientHeight - container.scrollTop;
      shouldStickRef.current = distance <= 80;
    };
    container.addEventListener('scroll', updateStickiness, { passive: true });

    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (shouldStickRef.current) scrollToBottom();
          });
    observer?.observe(container);
    if (contentRef.current) observer?.observe(contentRef.current);

    return () => {
      container.removeEventListener('scroll', updateStickiness);
      observer?.disconnect();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [containerRef, contentRef, enabled, identity, itemCount, scrollToBottom]);

  return { hold };
}
